'use client';
import React, { useEffect, useRef, useState } from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cn } from '../../lib/utils';

type TabsVariant = 'default' | 'bordered' | 'block';

interface TabsProps extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root> {
  variant?: TabsVariant;
}

const TabsContext = React.createContext<{ variant: TabsVariant }>({ variant: 'default' });

const Tabs = React.forwardRef<React.ElementRef<typeof TabsPrimitive.Root>, TabsProps>(
  ({ variant = 'default', children, className, ...props }, ref) => {
    return (
      <TabsContext.Provider value={{ variant }}>
        <TabsPrimitive.Root ref={ref} {...props} className={cn('w-full', className)}>
          {children}
        </TabsPrimitive.Root>
      </TabsContext.Provider>
    );
  }
);

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, children, ...props }, ref) => {
  const { variant } = React.useContext(TabsContext);
  const listRef = useRef<HTMLDivElement | null>(null);
  const [indicatorStyle, setIndicatorStyle] = useState<{
    left: number;
    top: number;
    width: number;
    height: number;
    opacity: number;
  }>({
    left: 0,
    top: 0,
    width: 0,
    height: 0,
    opacity: 0,
  });
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const isMountedRef = useRef(false);

  const updateIndicator = (animate = true) => {
    if (!listRef.current) return;
    const activeTab = listRef.current.querySelector<HTMLElement>('[data-state="active"]');
    if (!activeTab) {
      setIndicatorStyle(prev => (prev.opacity === 0 ? prev : { ...prev, opacity: 0 }));
      return;
    }

    const nextLeft = activeTab.offsetLeft;
    const nextTop = activeTab.offsetTop;
    const nextWidth = activeTab.offsetWidth;
    const nextHeight = activeTab.offsetHeight;

    setIndicatorStyle(prev => {
      if (
        prev.left === nextLeft &&
        prev.top === nextTop &&
        prev.width === nextWidth &&
        prev.height === nextHeight &&
        prev.opacity === 1
      ) {
        return prev;
      }
      return {
        left: nextLeft,
        top: nextTop,
        width: nextWidth,
        height: nextHeight,
        opacity: 1,
      };
    });

    if (animate && isMountedRef.current) {
      setShouldAnimate(true);
    }
  };

  const useIsomorphicLayoutEffect =
    typeof window !== 'undefined' ? React.useLayoutEffect : useEffect;

  useIsomorphicLayoutEffect(() => {
    // Initial synchronous positioning before paint — no animation
    updateIndicator(false);
  }, []);

  useEffect(() => {
    const listEl = listRef.current;
    if (!listEl) return;

    // After mount, mark mounted so subsequent changes animate
    isMountedRef.current = true;
    updateIndicator(false);
    requestAnimationFrame(() => {
      setShouldAnimate(true);
    });

    // ResizeObserver watches list and any child dimension change (e.g. counts loading from API)
    const resizeObserver = new ResizeObserver(() => {
      updateIndicator();
    });

    const observeChildren = () => {
      Array.from(listEl.children).forEach(child => {
        if (!child.hasAttribute('aria-hidden')) {
          resizeObserver.observe(child);
        }
      });
    };

    resizeObserver.observe(listEl);
    observeChildren();

    const mutationObserver = new MutationObserver(() => {
      observeChildren();
      updateIndicator();
    });

    mutationObserver.observe(listEl, {
      attributes: true,
      subtree: true,
      childList: true,
      characterData: true,
      attributeFilter: ['data-state'],
    });

    const handleResize = () => {
      updateIndicator();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      mutationObserver.disconnect();
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <TabsPrimitive.List
      ref={node => {
        listRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
      }}
      className={cn(
        'relative flex items-center',
        variant === 'default' &&
          'inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground',
        variant === 'bordered' && 'gap-5 h-12',
        variant === 'block' && 'gap-0 p-[3px] bg-muted rounded-[10px] w-fit',
        className
      )}
      {...props}
    >
      {/* Sliding indicator element */}
      {variant === 'bordered' && (
        <span
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute bottom-0 left-0 h-px bg-primary z-20',
            shouldAnimate &&
              'transition-[transform,width,opacity] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]'
          )}
          style={{
            transform: `translateX(${indicatorStyle.left}px)`,
            width: `${indicatorStyle.width}px`,
            opacity: indicatorStyle.opacity,
          }}
        />
      )}

      {variant === 'block' && (
        <span
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute top-0 left-0 rounded-[8px] bg-background shadow-sm',
            shouldAnimate &&
              'transition-[transform,width,height,opacity] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]'
          )}
          style={{
            transform: `translate3d(${indicatorStyle.left}px, ${indicatorStyle.top}px, 0)`,
            width: `${indicatorStyle.width}px`,
            height: `${indicatorStyle.height}px`,
            opacity: indicatorStyle.opacity,
          }}
        />
      )}

      {variant === 'default' && (
        <span
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute top-0 left-0 rounded-sm bg-background shadow-sm',
            shouldAnimate &&
              'transition-[transform,width,height,opacity] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]'
          )}
          style={{
            transform: `translate3d(${indicatorStyle.left}px, ${indicatorStyle.top}px, 0)`,
            width: `${indicatorStyle.width}px`,
            height: `${indicatorStyle.height}px`,
            opacity: indicatorStyle.opacity,
          }}
        />
      )}

      {children}
    </TabsPrimitive.List>
  );
});
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => {
  const { variant } = React.useContext(TabsContext);
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(
        'group relative z-10 flex items-center justify-center whitespace-nowrap h-full m-0 text-sm font-medium transition-colors duration-200 min-w-fit px-3 cursor-pointer focus-visible:outline-none disabled:cursor-not-allowed disabled:pointer-events-none disabled:opacity-50 [&_svg]:transition-colors [&_svg]:duration-200',
        variant === 'default' &&
          'inline-flex rounded-sm px-3 py-1.5 ring-offset-background focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 data-[state=active]:text-foreground',
        variant === 'bordered' &&
          'gap-1.5 px-0 text-muted-foreground hover:text-foreground data-[state=active]:text-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none',
        variant === 'block' &&
          'gap-1.5 px-3 py-1 text-muted-foreground data-[state=active]:text-foreground',
        className
      )}
      {...props}
    />
  );
});
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      className
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

Tabs.displayName = TabsPrimitive.Root.displayName;

export { TabsList, TabsTrigger, TabsContent };
export type { TabsProps, TabsVariant };
export default Tabs;
